function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useDrag, useDrop } from 'react-dnd';
import Icon from '../common/containers/icon';
export const CABINET_COLUMN_DND_TYPE = 'CABINET_COLUMN_FIELD';
const SWITCH_COLUMN_STYLE = {
  width: '2.75rem',
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center'
};
const FieldSwitch = ({
  id,
  checked,
  disabled: _disabled = false,
  onChange: _onChange = undefined
}) => /*#__PURE__*/React.createElement("div", {
  className: "t-col-customizer-switch-col",
  style: SWITCH_COLUMN_STYLE
}, /*#__PURE__*/React.createElement("div", {
  className: "form-check form-switch mb-0",
  style: {
    paddingLeft: 0,
    marginBottom: 0,
    minHeight: '1.25rem'
  }
}, /*#__PURE__*/React.createElement("input", {
  className: "form-check-input",
  type: "checkbox",
  role: "switch",
  id: id,
  checked: checked,
  disabled: _disabled,
  onChange: _onChange,
  style: {
    cursor: _disabled ? 'default' : 'pointer',
    marginLeft: 0,
    float: 'none'
  }
})));
export const fieldRowLayoutClass = 't-col-customizer-field-row d-flex align-items-center px-3 py-2';
export const fieldRowLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  flexGrow: 1,
  minWidth: 0,
  marginRight: '0.5rem',
  gap: '0.5rem'
};
export const fieldRowHandleStyle = {
  width: '1.25rem',
  flexShrink: 0
};
FieldSwitch.propTypes = {
  id: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func
};

/**
 * A row for something the cabinet always shows. Listed so the customizer is a
 * complete picture of what is on screen, but locked.
 */
const StaticRow = ({
  testIdPrefix,
  title
}) => {
  const safeTitle = title.replace(/[^A-Za-z0-9_-]/g, '-');
  return /*#__PURE__*/React.createElement("div", {
    className: `${fieldRowLayoutClass} text-muted`,
    "data-testid": `${testIdPrefix}-static-${safeTitle}`
  }, /*#__PURE__*/React.createElement("div", {
    style: fieldRowLabelStyle
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": true,
    style: fieldRowHandleStyle
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-truncate"
  }, title)), /*#__PURE__*/React.createElement(FieldSwitch, {
    id: `${testIdPrefix}-static-switch-${safeTitle}`,
    checked: true,
    disabled: true
  }));
};
StaticRow.propTypes = {
  testIdPrefix: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired
};

/**
 * A row the user switches on or off. Ordering is handled by the draggable row, so
 * this is used wherever a plain toggle is enough.
 */
const ToggleRow = ({
  testIdPrefix,
  itemKey,
  title,
  checked,
  onToggle
}) => /*#__PURE__*/React.createElement("div", {
  className: fieldRowLayoutClass,
  "data-testid": `${testIdPrefix}-${itemKey}`
}, /*#__PURE__*/React.createElement("div", {
  style: fieldRowLabelStyle
}, /*#__PURE__*/React.createElement("span", {
  "aria-hidden": true,
  style: fieldRowHandleStyle
}), /*#__PURE__*/React.createElement("span", {
  className: "text-truncate"
}, title)), /*#__PURE__*/React.createElement(FieldSwitch, {
  id: `${testIdPrefix}-switch-${itemKey}`,
  checked: checked,
  onChange: () => onToggle(itemKey)
}));
ToggleRow.propTypes = {
  testIdPrefix: PropTypes.string.isRequired,
  itemKey: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired
};
const CabinetColumnCustomizerDraggableRow = ({
  fieldKey,
  title,
  index,
  onOrderChange,
  onToggleOff
}) => {
  const ref = useRef(null);
  const [{
    handlerId
  }, drop] = useDrop({
    accept: CABINET_COLUMN_DND_TYPE,
    collect: monitor => ({
      handlerId: monitor.getHandlerId()
    }),
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        return;
      }
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      onOrderChange(dragIndex, hoverIndex);
      // eslint-disable-next-line no-param-reassign
      item.index = hoverIndex;
    }
  });
  const [{
    isDragging
  }, drag] = useDrag({
    type: CABINET_COLUMN_DND_TYPE,
    item: () => ({
      key: fieldKey,
      index
    }),
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });
  drag(drop(ref));
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    "data-handler-id": handlerId,
    "data-testid": `t-col-customizer-shown-${fieldKey}`,
    className: fieldRowLayoutClass,
    style: {
      opacity: isDragging ? 0.4 : 1
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: fieldRowLabelStyle
  }, /*#__PURE__*/React.createElement("span", {
    className: "t-col-customizer-drag-handle text-muted",
    style: _objectSpread(_objectSpread({}, fieldRowHandleStyle), {}, {
      cursor: 'grab'
    }),
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "bars"
  })), /*#__PURE__*/React.createElement("span", {
    className: "text-truncate"
  }, title)), /*#__PURE__*/React.createElement(FieldSwitch, {
    id: `t-col-customizer-switch-${fieldKey}`,
    checked: true,
    onChange: () => onToggleOff(fieldKey)
  }));
};
CabinetColumnCustomizerDraggableRow.propTypes = {
  fieldKey: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  onOrderChange: PropTypes.func.isRequired,
  onToggleOff: PropTypes.func.isRequired
};
export { FieldSwitch, StaticRow, ToggleRow };
export default CabinetColumnCustomizerDraggableRow;